import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { motion } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  CATEGORIES,
  CATEGORY_ORDER,
  convertFactor,
  convertAll,
  findUnit,
  convertTemperature,
  DATA_UNITS_DECIMAL,
  DATA_UNITS_BINARY,
  TEMP_UNITS,
  type CategoryKey,
  type FactorUnit,
  type DataMode,
  type TempUnit,
} from "@/lib/calculators/unitConverter";

type B = { en: string; bn: string };
type Lang = "en" | "bn";

const T = {
  h1: { en: "Unit Converter", bn: "ইউনিট কনভার্টার" },
  desc: {
    en: "All-in-one unit converter — length, weight, area, volume, temperature, time, speed, data, energy and pressure. Includes Bangladesh traditional units (mon, sher, tola, katha, bigha, hath).",
    bn: "এক জায়গায় সব ইউনিট কনভার্টার — দৈর্ঘ্য, ওজন, ক্ষেত্রফল, আয়তন, তাপমাত্রা, সময়, গতি, ডেটা, শক্তি ও চাপ। বাংলাদেশের ঐতিহ্যবাহী একক (মন, সের, তোলা, কাঠা, বিঘা, হাত) সহ।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  amount: { en: "Amount", bn: "পরিমাণ" },
  from: { en: "From", bn: "থেকে" },
  to: { en: "To", bn: "তে" },
  swap: { en: "Swap", bn: "বদল" },
  result: { en: "Result", bn: "ফলাফল" },
  showAll: { en: "Show all units", bn: "সব এককে দেখান" },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "What is unit conversion?", bn: "ইউনিট কনভার্সন কী?" },
    a: { en: "Converting a quantity from one unit of measurement to another while keeping its physical value the same — e.g., 1 meter expressed as 100 cm or 3.281 ft.",
         bn: "একই পরিমাণকে এক একক থেকে অন্য এককে প্রকাশ করা — যেমন ১ মিটার = ১০০ সেমি = ৩.২৮১ ফুট।" } },
  { q: { en: "1 kg = ? lb?", bn: "১ কেজি = কত পাউন্ড?" },
    a: { en: "1 kg = 2.20462 lb. 50 kg = 110.23 lb.", bn: "১ কেজি = ২.২০৪৬২ পাউন্ড। ৫০ কেজি = ১১০.২৩ পাউন্ড।" } },
  { q: { en: "1 km = ? miles?", bn: "১ কিমি = কত মাইল?" },
    a: { en: "1 km = 0.621371 miles. 100 km = 62.137 miles.", bn: "১ কিমি = ০.৬২১৩৭১ মাইল। ১০০ কিমি = ৬২.১৩৭ মাইল।" } },
  { q: { en: "1 katha = ? sq ft?", bn: "১ কাঠা = কত বর্গফুট?" },
    a: { en: "1 standard BD katha = 720 sq ft = 66.89 sq m. 20 katha = 1 bigha = 14,400 sq ft.",
         bn: "১ মান কাঠা = ৭২০ বর্গফুট = ৬৬.৮৯ বর্গমিটার। ২০ কাঠা = ১ বিঘা = ১৪,৪০০ বর্গফুট।" } },
  { q: { en: "1 mon = ? kg?", bn: "১ মন = কত কেজি?" },
    a: { en: "1 mon (maund) = 40 sher = 37.324 kg = 82.27 lb.",
         bn: "১ মন = ৪০ সের = ৩৭.৩২৪ কেজি = ৮২.২৭ পাউন্ড।" } },
  { q: { en: "১ কেজি = কত পাউন্ড?", bn: "১ কেজি = কত পাউন্ড?" },
    a: { en: "১ কেজি = ২.২০৪৬২ পাউন্ড।", bn: "১ কেজি = ২.২০৪৬২ পাউন্ড।" } },
  { q: { en: "0 °C = ? °F?", bn: "০ ডিগ্রি সেলসিয়াস = কত ফারেনহাইট?" },
    a: { en: "0 °C = 32 °F = 273.15 K. Body temp 37 °C = 98.6 °F.",
         bn: "০ °C = ৩২ °F = ২৭৩.১৫ K। শরীরের তাপ ৩৭ °C = ৯৮.৬ °F।" } },
  { q: { en: "1 GB = ? MB?", bn: "১ জিবি = কত এমবি?" },
    a: { en: "Decimal (storage / mobile data): 1 GB = 1,000 MB. Binary (RAM / OS): 1 GiB = 1,024 MiB. Most BD mobile data packs use the decimal definition.",
         bn: "দশমিক (স্টোরেজ / মোবাইল ডেটা): ১ জিবি = ১,০০০ এমবি। বাইনারি (র‌্যাম / OS): ১ GiB = ১,০২৪ MiB। বাংলাদেশের মোবাইল ডেটা প্যাকেজ সাধারণত দশমিক হিসাবে।" } },
  { q: { en: "Why is MB different on my phone vs PC?", bn: "মোবাইল ও পিসিতে এমবি ভিন্ন কেন?" },
    a: { en: "Storage manufacturers and mobile operators use decimal (1 GB = 10⁹ bytes). Operating systems often display binary (1 GiB = 2³⁰ bytes), making the same drive appear ~7% smaller.",
         bn: "স্টোরেজ ও অপারেটররা দশমিক (১ GB = ১০⁹ বাইট) ব্যবহার করে; OS অনেক সময় বাইনারি (১ GiB = ২³⁰) দেখায়, তাই ড্রাইভ ৭% ছোট মনে হয়।" } },
  { q: { en: "1 liter = ? US gallon?", bn: "১ লিটার = কত গ্যালন?" },
    a: { en: "1 L = 0.264 US gallon = 0.220 UK gallon. 1 US gallon = 3.785 L.",
         bn: "১ লিটার = ০.২৬৪ ইউএস গ্যালন = ০.২২০ ইম্পেরিয়াল গ্যালন। ১ ইউএস গ্যালন = ৩.৭৮৫ লিটার।" } },
];

const searchSchema = z.object({
  type: fallback(z.enum(CATEGORY_ORDER as [CategoryKey, ...CategoryKey[]]).optional(), "length").default("length"),
});

export const Route = createFileRoute("/unit-converter")({
  validateSearch: zodValidator(searchSchema),
  head: () => {
    const seo = buildSeo({
      title: "Unit Converter — Length, Weight, Area, Volume, Temp",
      description:
        "Free all-in-one unit converter for Bangladesh. Convert length, weight (kg, mon, sher), area (katha, bigha), volume, temperature, speed, time, data. ইউনিট কনভার্টার।",
      path: "/unit-converter",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Unit Converter", path: "/unit-converter" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: UnitConverterPage,
});

function stripBn(s: string): string {
  return s.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
}

function UnitConverterPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const { type = "length" } = Route.useSearch();
  const navigate = useNavigate({ from: "/unit-converter" });

  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 4) => {
    if (!Number.isFinite(n)) return "—";
    const rounded = Math.round(n * 10 ** d) / 10 ** d;
    const s = rounded.toLocaleString("en-US", { maximumFractionDigits: d });
    return useBn ? toBengaliNumerals(s) : s;
  };
  const fmtSmart = (n: number) => fmt(n, Math.abs(n) < 1 ? 6 : 4);

  const setTab = (k: CategoryKey) =>
    navigate({ search: { type: k }, replace: true });

  return (
    <CalculatorLayout
      slug="unit-converter"
      title={tr("h1")}
      description={tr("desc")}
      iconName="ArrowRightLeft"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Category tabs (scrollable) */}
      <div className="-mx-1 overflow-x-auto pb-1">
        <div className="flex w-max gap-1 rounded-xl border border-border bg-card p-1">
          {CATEGORY_ORDER.map((k) => {
            const c = CATEGORIES[k];
            return (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                className={cn(
                  "whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium transition-colors",
                  type === k ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
              >
                {lang === "en" ? c.en : c.bn}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        {type === "temperature" ? (
          <TemperaturePanel lang={lang} useBn={useBn} num={num} fmt={fmt} tr={tr} />
        ) : type === "data" ? (
          <DataPanel lang={lang} useBn={useBn} fmt={fmtSmart} tr={tr} />
        ) : (
          <FactorPanel
            key={type}
            categoryKey={type}
            lang={lang}
            useBn={useBn}
            fmt={fmtSmart}
            tr={tr}
          />
        )}
      </div>

      {/* Educational */}
      <article className="mt-12 space-y-10">
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Bangladesh traditional units — quick reference" : "বাংলাদেশের ঐতিহ্যবাহী একক — দ্রুত রেফারেন্স"}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
                  <th className="p-2 text-left">{lang === "en" ? "Type" : "ধরন"}</th>
                  <th className="p-2 text-left">{lang === "en" ? "Equivalent" : "সমতুল্য"}</th>
                </tr>
              </thead>
              <tbody className={cn("text-sm", lang === "bn" && "font-bn")}>
                <tr className="border-t border-border"><td className="p-2 font-medium">Hath / হাত</td><td className="p-2">{lang === "en" ? "Length" : "দৈর্ঘ্য"}</td><td className="p-2">1.5 ft = 0.4572 m</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Tola / ভরি</td><td className="p-2">{lang === "en" ? "Weight (gold)" : "ওজন (সোনা)"}</td><td className="p-2">11.664 g</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Sher / সের</td><td className="p-2">{lang === "en" ? "Weight" : "ওজন"}</td><td className="p-2">0.9331 kg ≈ 933 g</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Mon (Maund) / মন</td><td className="p-2">{lang === "en" ? "Weight" : "ওজন"}</td><td className="p-2">37.324 kg = 40 sher</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Katha / কাঠা</td><td className="p-2">{lang === "en" ? "Area" : "ক্ষেত্রফল"}</td><td className="p-2">720 sq ft = 66.89 m²</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Bigha / বিঘা</td><td className="p-2">{lang === "en" ? "Area" : "ক্ষেত্রফল"}</td><td className="p-2">14,400 sq ft = 20 katha</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Decimal (Shotok) / ডেসিমেল</td><td className="p-2">{lang === "en" ? "Area" : "ক্ষেত্রফল"}</td><td className="p-2">435.6 sq ft = 1/100 acre</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Kani / কানি</td><td className="p-2">{lang === "en" ? "Area (Chittagong)" : "ক্ষেত্রফল (চট্টগ্রাম)"}</td><td className="p-2">17,424 sq ft = 40 decimal</td></tr>
                <tr className="border-t border-border"><td className="p-2 font-medium">Chain / চেইন</td><td className="p-2">{lang === "en" ? "Length (surveys)" : "দৈর্ঘ্য (জরিপ)"}</td><td className="p-2">66 ft = 20.117 m</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Metric vs Imperial vs SI" : "মেট্রিক, ইম্পেরিয়াল ও SI"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Bangladesh officially uses the metric system (kg, m, L, °C). The Imperial system (lb, ft, gallon, °F) is common in the US and shipping documents. SI (Système International) is the global scientific standard built on seven base units: meter, kilogram, second, ampere, kelvin, mole, candela."
              : "বাংলাদেশ আনুষ্ঠানিকভাবে মেট্রিক পদ্ধতি (কেজি, মিটার, লিটার, °C) ব্যবহার করে। ইম্পেরিয়াল (পাউন্ড, ফুট, গ্যালন, °F) মার্কিন ও শিপিং কাগজপত্রে প্রচলিত। SI আন্তর্জাতিক বৈজ্ঞানিক মান — সাতটি মৌলিক একক: মিটার, কিলোগ্রাম, সেকেন্ড, অ্যাম্পিয়ার, কেলভিন, মোল, ক্যান্ডেলা।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Mobile data: MB vs MiB" : "মোবাইল ডেটা: MB ও MiB"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Bangladeshi mobile operators (GP, Robi, Banglalink, Teletalk) sell data in decimal MB / GB (1 GB = 1,000 MB = 10⁹ bytes). Your phone's OS may report usage in binary MiB / GiB (1 GiB = 1,024 MiB = 2³⁰ bytes), making the package look ~7% smaller. Both refer to the same bytes — just different scales."
              : "GP, রবি, বাংলালিংক, টেলিটক সব দশমিক MB/GB ব্যবহার করে (১ GB = ১,০০০ MB = ১০⁹ বাইট)। ফোনের OS অনেক সময় বাইনারি MiB/GiB হিসাবে দেখায় (১ GiB = ১,০২৪ MiB = ২³⁰ বাইট), ফলে প্যাকেজ ~৭% ছোট মনে হয়। বাইটের সংখ্যা একই, শুধু স্কেল ভিন্ন।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Specialized converters" : "বিশেষায়িত কনভার্টার"}
          </h2>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/cm-to-feet" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">CM ↔ Feet</Link>
            <Link to="/feet-to-meter" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Feet ↔ Meter" : "ফুট ↔ মিটার"}</Link>
            <Link to="/kg-to-pounds" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "KG ↔ Pounds" : "কেজি ↔ পাউন্ড"}</Link>
            <Link to="/land-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Land" : "জমি"}</Link>
            <Link to="/square-feet-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Square feet" : "বর্গফুট"}</Link>
            <Link to="/distance-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Distance" : "দূরত্ব"}</Link>
            <Link to="/gbp-to-usd" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">GBP ↔ USD</Link>
          </div>
        </section>
      </article>
    </CalculatorLayout>
  );
}

/* -------------------- Factor-based panel (length / weight / area / volume / time / speed / energy / pressure) -------------------- */

function FactorPanel({
  categoryKey, lang, useBn, fmt, tr,
}: {
  categoryKey: CategoryKey; lang: Lang; useBn: boolean;
  fmt: (n: number) => string;
  tr: (k: keyof typeof T) => string;
}) {
  const cat = CATEGORIES[categoryKey];
  const [from, setFrom] = useState(cat.defaultFrom);
  const [to, setTo] = useState(cat.defaultTo);
  const [amount, setAmount] = useState(cat.defaultAmount);
  const [showAll, setShowAll] = useState(false);

  const fromU = findUnit(cat.units, from);
  const toU = findUnit(cat.units, to);
  const value = parseFloat(amount) || 0;

  const result = useMemo(() => convertFactor(value, fromU, toU), [value, fromU, toU]);
  const all = useMemo(() => (showAll ? convertAll(value, fromU, cat.units) : []), [value, fromU, cat.units, showAll]);

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <PanelShell
      lang={lang} useBn={useBn} tr={tr}
      amount={amount} onAmount={setAmount}
      from={from} to={to} setFrom={setFrom} setTo={setTo}
      units={cat.units}
      onSwap={swap}
      showAll={showAll} setShowAll={setShowAll}
      related={cat.related}
    >
      <ResultBlock
        lang={lang}
        primary={`${fmt(result)}`}
        unitLabel={lang === "en" ? toU?.shortEn ?? "" : toU?.shortBn ?? ""}
        sub={`${fmt(value)} ${lang === "en" ? fromU?.en ?? "" : fromU?.bn ?? ""} = ${fmt(result)} ${lang === "en" ? toU?.en ?? "" : toU?.bn ?? ""}`}
      />

      {showAll && (
        <AllUnitsTable
          lang={lang}
          rows={all.map(({ unit, value }) => ({
            label: lang === "en" ? unit.en : unit.bn,
            short: lang === "en" ? unit.shortEn : unit.shortBn,
            value: fmt(value),
            note: unit.note ? (lang === "en" ? unit.note.en : unit.note.bn) : undefined,
            highlight: unit.key === to,
          }))}
        />
      )}
    </PanelShell>
  );
}

/* -------------------- Temperature panel -------------------- */

function TemperaturePanel({
  lang, useBn, num, fmt, tr,
}: { lang: Lang; useBn: boolean; num: (n: number | string) => string; fmt: (n: number, d?: number) => string; tr: (k: keyof typeof T) => string }) {
  const [from, setFrom] = useState<TempUnit>("C");
  const [to, setTo] = useState<TempUnit>("F");
  const [amount, setAmount] = useState("37");
  const value = parseFloat(amount) || 0;
  const result = convertTemperature(value, from, to);
  const all = TEMP_UNITS.map((u) => ({ unit: u, value: convertTemperature(value, from, u.key) }));
  const swap = () => { setFrom(to); setTo(from); };

  const fromU = TEMP_UNITS.find((u) => u.key === from)!;
  const toU = TEMP_UNITS.find((u) => u.key === to)!;

  return (
    <div className="space-y-6">
      <div className="grid items-end gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
        <Field label={tr("amount")} useBn={useBn}>
          <input type="text" inputMode="decimal" value={useBn ? toBengaliNumerals(amount) : amount}
            onChange={(e) => setAmount(stripBn(e.target.value))}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-2xl font-semibold tabular-nums focus:border-accent focus:outline-none" />
        </Field>
        <Field label={tr("from")} useBn={useBn}>
          <SimpleSelect value={from} onChange={(v) => setFrom(v as TempUnit)}>
            {TEMP_UNITS.map((u) => <option key={u.key} value={u.key}>{(lang === "en" ? u.en : u.bn) + ` (${u.symbol})`}</option>)}
          </SimpleSelect>
        </Field>
        <SwapBtn onClick={swap} label={tr("swap")} />
        <Field label={tr("to")} useBn={useBn}>
          <SimpleSelect value={to} onChange={(v) => setTo(v as TempUnit)}>
            {TEMP_UNITS.map((u) => <option key={u.key} value={u.key}>{(lang === "en" ? u.en : u.bn) + ` (${u.symbol})`}</option>)}
          </SimpleSelect>
        </Field>
        <div />
      </div>

      <ResultBlock
        lang={lang}
        primary={fmt(result, 3)}
        unitLabel={toU.symbol}
        sub={`${fmt(value, 3)} ${fromU.symbol} = ${fmt(result, 3)} ${toU.symbol}`}
      />

      <AllUnitsTable
        lang={lang}
        rows={all.map(({ unit, value }) => ({
          label: (lang === "en" ? unit.en : unit.bn),
          short: unit.symbol,
          value: fmt(value, 3),
          highlight: unit.key === to,
        }))}
      />

      <div className={cn("rounded-xl border border-border bg-card p-4 text-sm", lang === "bn" && "font-bn")}>
        <p className="font-semibold">{lang === "en" ? "Temperature formulas" : "তাপমাত্রার সূত্র"}</p>
        <ul className="mt-2 ml-5 list-disc space-y-1 text-muted-foreground">
          <li>°F = °C × 9/5 + 32</li>
          <li>°C = (°F − 32) × 5/9</li>
          <li>K = °C + 273.15</li>
          <li>{lang === "en" ? "Water freezes 0 °C / 32 °F; boils 100 °C / 212 °F. Body temp 37 °C / 98.6 °F." : "পানি জমে ০ °C / ৩২ °F; ফোটে ১০০ °C / ২১২ °F। শরীরের তাপ ৩৭ °C / ৯৮.৬ °F।"}</li>
        </ul>
      </div>
    </div>
  );
}

/* -------------------- Data storage panel -------------------- */

function DataPanel({
  lang, useBn, fmt, tr,
}: { lang: Lang; useBn: boolean; fmt: (n: number) => string; tr: (k: keyof typeof T) => string }) {
  const [mode, setMode] = useState<DataMode>("decimal");
  const units = mode === "decimal" ? DATA_UNITS_DECIMAL : DATA_UNITS_BINARY;
  const [from, setFrom] = useState("GB");
  const [to, setTo] = useState("MB");
  const [amount, setAmount] = useState("1");
  const [showAll, setShowAll] = useState(false);

  // when switching mode, snap from/to into the new vocabulary
  const ensureKey = (k: string): string => units.find((u) => u.key === k) ? k : units[1]?.key ?? units[0].key;
  const fromKey = ensureKey(from);
  const toKey = ensureKey(to);
  const fromU = findUnit(units, fromKey);
  const toU = findUnit(units, toKey);

  const value = parseFloat(amount) || 0;
  const result = convertFactor(value, fromU, toU);
  const all = showAll ? convertAll(value, fromU, units) : [];

  const swap = () => { setFrom(toKey); setTo(fromKey); };

  return (
    <div className="space-y-6">
      {/* Mode toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-border bg-card p-1">
          {(["decimal", "binary"] as DataMode[]).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={cn("rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                mode === m ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                lang === "bn" && "font-bn")}>
              {m === "decimal" ? (lang === "en" ? "Decimal (1 KB = 1000 B)" : "দশমিক (১ KB = ১০০০ B)") : (lang === "en" ? "Binary (1 KiB = 1024 B)" : "বাইনারি (১ KiB = ১০২৪ B)")}
            </button>
          ))}
        </div>
        <span className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          {mode === "decimal" ? (lang === "en" ? "Used by storage manufacturers & mobile data plans." : "স্টোরেজ ও মোবাইল ডেটা প্যাকেজে ব্যবহৃত।") : (lang === "en" ? "Used by operating systems & RAM." : "অপারেটিং সিস্টেম ও র‌্যামে ব্যবহৃত।")}
        </span>
      </div>

      <PanelShell
        lang={lang} useBn={useBn} tr={tr}
        amount={amount} onAmount={setAmount}
        from={fromKey} to={toKey}
        setFrom={setFrom} setTo={setTo}
        units={units}
        onSwap={swap}
        showAll={showAll} setShowAll={setShowAll}
      >
        <ResultBlock
          lang={lang}
          primary={fmt(result)}
          unitLabel={toU?.shortEn ?? ""}
          sub={`${fmt(value)} ${fromU?.shortEn} = ${fmt(result)} ${toU?.shortEn}`}
        />
        {showAll && (
          <AllUnitsTable
            lang={lang}
            rows={all.map(({ unit, value }) => ({
              label: lang === "en" ? unit.en : unit.bn,
              short: unit.shortEn,
              value: fmt(value),
              highlight: unit.key === toKey,
            }))}
          />
        )}
      </PanelShell>
    </div>
  );
}

/* -------------------- Shared layout pieces -------------------- */

function PanelShell({
  lang, useBn, tr, amount, onAmount, from, to, setFrom, setTo, units,
  onSwap, showAll, setShowAll, related, children,
}: {
  lang: Lang; useBn: boolean; tr: (k: keyof typeof T) => string;
  amount: string; onAmount: (v: string) => void;
  from: string; to: string; setFrom: (s: string) => void; setTo: (s: string) => void;
  units: FactorUnit[];
  onSwap: () => void;
  showAll: boolean; setShowAll: (v: boolean) => void;
  related?: { to: string; en: string; bn: string };
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div className="grid items-end gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
        <Field label={tr("amount")} useBn={useBn}>
          <input type="text" inputMode="decimal" value={useBn ? toBengaliNumerals(amount) : amount}
            onChange={(e) => onAmount(stripBn(e.target.value))}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-2xl font-semibold tabular-nums focus:border-accent focus:outline-none" />
        </Field>
        <Field label={tr("from")} useBn={useBn}>
          <UnitSelect value={from} onChange={setFrom} units={units} lang={lang} />
        </Field>
        <SwapBtn onClick={onSwap} label={tr("swap")} />
        <Field label={tr("to")} useBn={useBn}>
          <UnitSelect value={to} onChange={setTo} units={units} lang={lang} />
        </Field>
        <div />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("showAll")}
        </label>
        {related && (
          <Link to={related.to} className="text-xs text-accent hover:underline">
            {lang === "en" ? `Detailed: ${related.en} →` : `বিস্তারিত: ${related.bn} →`}
          </Link>
        )}
      </div>

      {children}
    </div>
  );
}

function Field({ label, useBn, children }: { label: string; useBn: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-xs font-medium text-muted-foreground", useBn && "font-bn")}>{label}</label>
      {children}
    </div>
  );
}

function UnitSelect({ value, onChange, units, lang }: { value: string; onChange: (v: string) => void; units: FactorUnit[]; lang: Lang }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className={cn("w-full rounded-xl border border-border bg-card px-3 py-3 text-base font-medium focus:border-accent focus:outline-none", lang === "bn" && "font-bn")}>
      {units.map((u) => (
        <option key={u.key} value={u.key}>
          {lang === "en" ? `${u.en} (${u.shortEn})` : `${u.bn} (${u.shortBn})`}
        </option>
      ))}
    </select>
  );
}

function SimpleSelect({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-border bg-card px-3 py-3 text-base font-medium focus:border-accent focus:outline-none">
      {children}
    </select>
  );
}

function SwapBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <div className="flex items-center justify-center pb-2">
      <button type="button" onClick={onClick} aria-label={label}
        className="rounded-full border border-border bg-card p-2 hover:border-accent">
        <ArrowLeftRight size={16} />
      </button>
    </div>
  );
}

function ResultBlock({ lang, primary, unitLabel, sub }: { lang: Lang; primary: string; unitLabel: string; sub: string }) {
  return (
    <motion.div
      key={`${primary}-${unitLabel}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center"
    >
      <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
        {lang === "en" ? "Result" : "ফলাফল"}
      </p>
      <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
        {primary} <span className="text-base font-normal text-muted-foreground">{unitLabel}</span>
      </p>
      <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{sub}</p>
    </motion.div>
  );
}

function AllUnitsTable({ lang, rows }: { lang: Lang; rows: { label: string; short: string; value: string; note?: string; highlight?: boolean }[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
          <tr>
            <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
            <th className="p-2 text-right">{lang === "en" ? "Value" : "মান"}</th>
            <th className="p-2 text-left">{lang === "en" ? "Note" : "নোট"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={cn("border-t border-border", r.highlight && "bg-accent/5")}>
              <td className="p-2">
                <span className="font-medium">{r.label}</span>{" "}
                <span className="text-xs text-muted-foreground">({r.short})</span>
              </td>
              <td className="p-2 text-right tabular-nums">{r.value}</td>
              <td className={cn("p-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{r.note ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
